import { useEffect, useState } from 'react';
import momentTimezone from 'moment-timezone';
import { Col, Form, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Store } from '../../state/store.interfaces';
import { LrvForm } from '../../common/components/LrvForm/LrvForm';
import { LrvText } from '../../common/components/LrvText/LrvText';
import { useFormErrorFields } from '../../hooks/useFormErrorFields';
import { LrvModal } from '../../common/components/LrvModal/LrvModal';
import { LrvSwitch } from '../../common/components/LrvSwitch/LrvSwitch';
import { getUnitPhaseTypeFromAnalysis } from '../../helpers/units.helpers';
import { LrvInputV2 } from '../../common/components/LrvInputV2/LrvInputV2';
import { LrvSelectV2 } from '../../common/components/LrvSelectV2/LrvSelectV2';
import { getMainRole, PROVINCES_ECUADOR, roles, unitStatuses } from '../../config/commons';

import styles from './Units.module.scss';
import * as campusSlice from './campusSlice';

export const EditUnitForm = () => {
  const [t] = useTranslation();
  const { Option } = Select;
  const dispatch = useDispatch();
  const timezones = momentTimezone.tz.names();

  const [form] = Form.useForm();
  const { errorFields, resetErrorFields, extractAndSetErrorFields } = useFormErrorFields({ form });

  const [newCampusStatus, setNewCampusStatus] = useState(unitStatuses.ACTIVE);
  const [newCampusName, setNewCampusName] = useState('');
  const [newCampusCode, setNewCampusCode] = useState('');
  const [newCampusType, setNewCampusType] = useState('');
  const [newCampusTimezone, setNewCampusTimezone] = useState(momentTimezone.tz.guess());
  const [newCampusProvince, setNewCampusProvince] = useState('');

  const {
    selectedCampus,
    isCampusLoading,
    showUpdateCampusModal,
  } = useSelector((state: Store) => state.campus);

  const { company, phaseType } = useSelector((state: Store) => state.header);
  const unitPhaseType = getUnitPhaseTypeFromAnalysis(phaseType);

  const isCampusInactive = selectedCampus.status === unitStatuses.INACTIVE;
  const isNewCampusInactive = newCampusStatus === unitStatuses.INACTIVE;

  const higherRole = getMainRole();

  const MAX_CODES_LENGTH = 4;

  useEffect(() => {
    if (!selectedCampus?._id) {
      return;
    }

    setNewCampusStatus(selectedCampus.status);
    setNewCampusName(selectedCampus.name);
    setNewCampusCode(selectedCampus.code);
    setNewCampusType(selectedCampus.phaseType);
    setNewCampusTimezone(selectedCampus.timezone);
    setNewCampusProvince(selectedCampus.province);

    form.setFieldsValue({
      campusStatus: selectedCampus.status,
      campusName: selectedCampus.name,
      campusCode: selectedCampus.code,
      campusTimezone: selectedCampus.timezone,
      campusProvince: selectedCampus.province,
    });
  }, [selectedCampus._id, selectedCampus.code, selectedCampus.name, selectedCampus.phaseType, selectedCampus.timezone, selectedCampus.province]);

  const childrenTimezone: JSX.Element[] = [];
  for (let i = 0; i < timezones.length; i++) {
    const label = ' (GMT' + momentTimezone.tz(timezones[i]).format('Z') + ') ' + timezones[i];
    childrenTimezone.push(<Option key={timezones[i]} value={timezones[i]}>{label}</Option>);
  }

  const childrenProvinces: JSX.Element[] = [];
  for (let i = 0; i < PROVINCES_ECUADOR.length; i++) {
    const province = PROVINCES_ECUADOR[i];
    childrenProvinces.push(<Option key={province} value={province}>{province}</Option>);
  }

  function checkDisabledButton () {
    return isNewCampusInactive && (isCampusLoading || isCampusInactive);
  }

  const renderCampusName = () => {
    const hasError = errorFields.includes('campusName');

    return (
      <Form.Item
        name='campusName'
        rules={[{ required: true, message: t('campus.nameRequired') }]}
      >
        <LrvInputV2
          label={t('campus.name')}
          theme='light'
          autoFocus
          value={newCampusName}
          hasError={hasError}
          onChange={(e) => setNewCampusName(e.target.value)}
          disabled={isCampusInactive && isNewCampusInactive}
        />
      </Form.Item>
    );
  };

  const renderCampusCode = () => {
    const hasError = errorFields.includes('campusCode');

    return (
      <Form.Item
        required
        name='campusCode'
        rules={[{ required: true, message: t('campus.codeRequired') }]}
      >
        <LrvInputV2
          label={t('campus.code')}
          theme='light'
          value={newCampusCode}
          className={styles.inputUpperCase}
          maxLength={MAX_CODES_LENGTH}
          onChange={(e) => setNewCampusCode(e.target.value)}
          disabled={isCampusInactive && isNewCampusInactive}
          hasError={hasError}
        />
      </Form.Item>
    );
  };

  const renderCampusTimezone = () => {
    const hasError = errorFields.includes('campusTimezone');

    return (
      <Form.Item
        name='campusTimezone'
        rules={[{ required: true }]}
      >
        <LrvSelectV2
          label={t('campus.timezone')}
          theme='light'
          showSearch
          value={newCampusTimezone}
          defaultValue={newCampusTimezone}
          onChange={e => setNewCampusTimezone(e)}
          hasError={hasError}
          disabled={isCampusInactive && isNewCampusInactive}
        >
          {childrenTimezone}
        </LrvSelectV2>
      </Form.Item>
    );
  };

  const renderCampusProvince = () => {
    if (company.isInternational) {
      return null;
    }
    const hasError = errorFields.includes('campusProvince');

    return (
      <Form.Item
        required
        name='campusProvince'
        rules={[{ required: true, message: t('common.requiredField') }]}
      >
        <LrvSelectV2
          label={t('campus.province')}
          theme='light'
          showSearch
          value={newCampusProvince}
          defaultValue={newCampusProvince}
          onChange={e => setNewCampusProvince(e)}
          hasError={hasError}
        >
          {childrenProvinces}
        </LrvSelectV2>
      </Form.Item>
    );
  };

  const renderCampusType = () => {
    const hasError = errorFields.includes('campusType');

    return (
      <Form.Item
        name='campusType'
        valuePropName='defaultValue'
        initialValue={t(`stockings.phaseTypes.${newCampusType}`)}
      >
        <LrvInputV2
          label={t('campus.phase')}
          theme='light'
          value={t(`stockings.phaseTypes.${newCampusType}`) + ''}
          readOnly
          style={{ color: 'grey' }}
          disabled={isCampusInactive && isNewCampusInactive}
          hasError={hasError}
        />
      </Form.Item>
    );
  };

  return (
    <LrvModal
      theme='light'
      className='editCampusModal'
      title={t('campus.editCampus')}
      isLoading={isCampusLoading}
      open={showUpdateCampusModal}
      destroyOnClose={true}
      okButtonProps={{
        disabled: checkDisabledButton(),
        form: 'formEditCampus',
        id: 'formEditCampus_btnSave',
      }}
      onOk={() => {
        form.setFieldsValue({
          campusStatus: newCampusStatus,
          campusName: newCampusName,
          campusCode: newCampusCode,
          campusTimezone: newCampusTimezone,
          campusProvince: newCampusProvince,
        });

        form.validateFields()
          .then(() => {
            form.submit();
          });
      }}
      onCancel={() => {
        dispatch(campusSlice.setShowUpdateCampusModal(false));
        resetErrorFields();
      }}
      okText={t('campus.save')}
      cancelText={t('campus.cancel')}
    >
      <LrvForm
        theme='light'
        form={form}
        layout='vertical'
        name='formEditCampus'
        id='formEditCampus'
        onFinish={() => {
          const campusData = { _id: selectedCampus._id, status: newCampusStatus, name: newCampusName, code: newCampusCode, timezone: newCampusTimezone, province: newCampusProvince, companyId: selectedCampus.companyId, phaseType: unitPhaseType };
          dispatch(campusSlice.updateCampus(campusData));

          setNewCampusStatus('');
          setNewCampusName('');
          setNewCampusCode('');
          setNewCampusTimezone('');
          setNewCampusProvince('');
          form.resetFields();

          dispatch(campusSlice.setSelectedCampus({ _id: '', name: '', code: '', timezone: '', companyId: '', country: '', province: '', phaseType: '', status: '' }));
          dispatch(campusSlice.setShowUpdateCampusModal(false));
          resetErrorFields();
        }}
        onFieldsChange={extractAndSetErrorFields}
      >
        <Form.Item
          required
          name='campusStatus'
          valuePropName='defaultValue'
          initialValue={newCampusStatus}
        >
          <Row align='middle' justify='space-between' wrap>
            <Row align='middle' justify='start' wrap>
              <LrvText
                theme='light'
                text={selectedCampus.name}
                className={styles.campusTitle}
              />
            </Row>
            <Row>
              <Col>
                <LrvText
                  theme='light'
                  text={t('campus.active')}
                />

              </Col>
              {(higherRole === roles.CLIENT_MANAGER || higherRole === roles.CLIENT_OPERATOR) ? null :
                <Col>
                  <LrvSwitch
                    theme='light'
                    className={styles.switch}
                    id='campus_status'
                    checked={newCampusStatus === unitStatuses.ACTIVE}
                    onChange={(value) => {
                      if (!value) {
                        setNewCampusStatus(unitStatuses.ACTIVE);
                      }
                      setNewCampusStatus(value ? unitStatuses.ACTIVE : unitStatuses.INACTIVE);
                    }}
                  />
                </Col>
              }
            </Row>
          </Row>
        </Form.Item>

        {renderCampusName()}
        {renderCampusCode()}
        {renderCampusTimezone()}
        {renderCampusProvince()}
        {renderCampusType()}
      </LrvForm>
    </LrvModal>
  );
};
